import { create } from "zustand";
import { persist } from "zustand/middleware";

export type PaginationLimit = 10 | 25 | 50 | 100;

export const PAGINATION_KEYS = {
  TRANSACTION_LIST: "TRANSACTION_LIST_PAGE",
  INVOICE_LIST: "INVOICE_LIST_PAGE",
  CUSTOMER_LIST: "CUSTOMER_LIST_PAGE",
  CLIENT_PORTAL_CUSTOMER_LIST: "CLIENT_PORTAL_CUSTOMER_LIST_PAGE",
  CUSTOMER_DETAIL_INVOICE_LIST: "CUSTOMER_DETAIL_INVOICE_LIST",
  CUSTOMER_DETAIL_EMAIL_HISTORY: "CUSTOMER_DETAIL_EMAIL_HISTORY_LIST",
  CUSTOMER_DETAIL_CUSTOM_FIELDS: "CUSTOMER_DETAIL_CUSTOM_FIELDS_TAB",
  UNMAPPED_CUSTOMERS_LIST: "UNMAPPED_CUSTOMERS_LIST_PAGE",
  INTEGRATED_CUSTOMERS_LIST: "INTEGRATED_CUSTOMERS_LIST_PAGE",
  PAYOUT_LIST: "PAYOUT_LIST_PAGE",
  ADMIN_TEAM_LIST: "ADMIN_TEAM_LIST_PAGE",
  USER_MANAGEMENT: "USER_MANAGEMENT_PAGE",
  CLIENT_PORTAL_INVOICE_LIST: "CLIENT_PORTAL_INVOICE_LIST_PAGE",
  UNMAPPED_CUSTOMERS_TRANSACTIONS_MODAL: "UNMAPPED_CUSTOMERS_TRANSACTIONS_LIST_MODAL",
  ADMIN_TEAM_PAYOUT_LIST: "ADMIN_TEAM_PAYOUT_LIST_PAGE",
  ADMIN_TEAM_TRANSFER_LIST: "ADMIN_TEAM_TRANSFER_LIST_PAGE"
} as const;

export interface PagePaginationState {
  limit: PaginationLimit;
  page: number;
}

interface PaginationState {
  pages: Record<string, PagePaginationState>;

  setPageState: (pageKey: string, state: Partial<PagePaginationState>) => void;
  getPageState: (
    pageKey: string,
    defaultState?: Partial<PagePaginationState>
  ) => PagePaginationState;
  setLimit: (pageKey: string, limit: PaginationLimit) => void;
  setPage: (pageKey: string, page: number) => void;
  resetPageState: (pageKey: string) => void;
  resetAllPages: () => void;
}

const createDefaultPageState = (overrides?: Partial<PagePaginationState>): PagePaginationState => ({
  limit: 25,
  page: 1,
  ...overrides
});

export const usePaginationStore = create<PaginationState>()(
  persist(
    (set, get) => ({
      pages: {},

      setPageState: (pageKey: string, newState: Partial<PagePaginationState>) => {
        set(state => ({
          pages: {
            ...state.pages,
            [pageKey]: {
              ...createDefaultPageState(),
              ...state.pages[pageKey],
              ...newState
            }
          }
        }));
      },

      getPageState: (pageKey: string, defaultState?: Partial<PagePaginationState>) => {
        const state = get();
        return {
          ...createDefaultPageState(defaultState),
          ...state.pages[pageKey]
        };
      },

      setLimit: (pageKey: string, limit: PaginationLimit) => {
        get().setPageState(pageKey, { limit });
      },

      setPage: (pageKey: string, page: number) => {
        get().setPageState(pageKey, { page });
      },

      resetPageState: (pageKey: string) => {
        set(state => {
          const newPages = { ...state.pages };
          delete newPages[pageKey];
          return { pages: newPages };
        });
      },

      resetAllPages: () => {
        set({ pages: {} });
      }
    }),
    {
      name: "pagination-store",
      partialize: state => ({
        pages: Object.fromEntries(
          Object.entries(state.pages).map(([key, value]) => [key, { limit: value.limit }])
        )
      })
    }
  )
);

export const usePagination = (pageKey: string, defaultState?: Partial<PagePaginationState>) => {
  const store = usePaginationStore();
  const pageState = store.getPageState(pageKey, defaultState);

  return {
    ...pageState,
    setLimit: (limit: PaginationLimit) => store.setLimit(pageKey, limit),
    setPage: (page: number) => store.setPage(pageKey, page),
    setState: (state: Partial<PagePaginationState>) => store.setPageState(pageKey, state),
    resetState: () => store.resetPageState(pageKey)
  };
};

export const usePaginationLimit = (pageKey: string, defaultLimit: PaginationLimit = 25) => {
  const { limit, setLimit } = usePagination(pageKey, { limit: defaultLimit });
  return { limit, setLimit };
};
